import { ApolloClient, InMemoryCache, ApolloLink, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { redirectToSystemUnavailable } from '../routes/navigation';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';

const httpLink = createHttpLink({ uri: (process.env.REACT_APP_GRAPHQL_ENDPOINT ?? '').replace(/\/$/, '') + '/graphql' });

const UNAUTH_OPERATIONS = new Set(['Login', 'CriarUsuarioEAtleta', 'SolicitarRecuperacaoSenha', 'RedefinirSenha']);

const authLink = setContext((operation, { headers }) => {
  if (operation.operationName && UNAUTH_OPERATIONS.has(operation.operationName)) {
    return { headers };
  }

  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: 5000,
    jitter: true,
  },
  attempts: {
    max: 2,
    retryIf: (error) => {
      // Só retenta em erros de rede (timeout, connection refused, etc)
      // NÃO retenta em erros de validação GraphQL
      return !!error && !error.statusCode;
    },
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // 401 from GraphQL should logout and go to login
  if ((networkError as any)?.statusCode === 401) {
    dispatchLogout();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    return;
  }

  // Operações de auth não redirecionam para system-unavailable
  if (networkError && operation.operationName && UNAUTH_OPERATIONS.has(operation.operationName)) {
    return;
  }

  // Any networkError (including 5xx, timeouts, fetch failures) → system-unavailable
  // O RetryLink já tentou 3x antes de chegar aqui
  if (networkError) {
    redirectToSystemUnavailable();
    return;
  }

  // Operações que tratam o erro localmente (ex.: checkout PIX, que exibe uma mensagem
  // genérica de suporte) sinalizam via context para não vazar o texto técnico do
  // backend no alerta global.
  if (operation.getContext()?.suppressGlobalError) {
    return;
  }

  // Otherwise, surface GraphQL errors normally
  let message = 'Erro inesperado. Tente novamente.';
  if (graphQLErrors && graphQLErrors.length > 0) {
    message = graphQLErrors[0].message;
  }
  dispatchGlobalError(message);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([retryLink, errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});
